import React, { useEffect, useReducer, useRef } from "react";
import { useBoardSkin } from "../../context/boardSkin";
import TetrisBoard3D from "../../components/three/TetrisBoard3D";
import { getBlockColor } from "../../components/three/materials/SpectrumMaterial";
import styles from "./player.module.css";


// Spectrum skin accent color - matches the 3D aesthetic (fallback)
const SPECTRUM_BORDER_COLOR = "#00FFFF";

const Player3DView = ({ player, game }) => {
  const { skinName, selectedPremiumBoard } = useBoardSkin();
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  // Persistent border color ref for smooth transitions
  const borderColorRef = useRef(SPECTRUM_BORDER_COLOR);

  // Connect player's renderUi to force re-render
  useEffect(() => {
    player.renderUi = forceUpdate;
  }, [player]);

  // Update border color based on current tetromino (for Spectrum theme only)
  if (selectedPremiumBoard === 'spectrum' && player.currentTetromino) {
    const newColor = getBlockColor(player.currentTetromino.colorClass);
    if (newColor) {
      borderColorRef.current = newColor;
    }
  }

  const labelStyle = {
    fontSize: "8px",
    fontWeight: "bold",
    fontStyle: "italic",
    fontFamily: '"Courier New", monospace',
    color: "#aaa",
    marginTop: "1px",
    letterSpacing: "0.5px",
  };

  const valueStyle = {
    fontSize: "14px",
    fontWeight: "bold",
    fontStyle: "italic",
    fontFamily: '"Courier New", monospace',
    color: "#aaa",
    lineHeight: 1,
    textShadow: "1px 1px 2px rgba(0,0,0,0.8)",
  };

  // Dynamic style for info-item boxes (Spectrum theme only)
  const infoItemDynamicStyle = selectedPremiumBoard === 'spectrum' ? {
    backgroundColor: `${borderColorRef.current}33`, // 33 = 20% opacity in hex
    boxShadow: `0 0 5px ${borderColorRef.current}`,
  } : {};

  return (
    <section
      className={`${styles["player-section"]} ${
        player.destroyInAction ? styles["shaking"] : ""
      }`}
    >
      {player.isGameOver && (
        <div className={styles["game-over-overlay"]}>game over</div>
      )}

      {/* Game Info - Same layout as 2D but styled for 3D */}
      <div
        className={`${styles["game-info"]} ${styles["game-info-board-" + selectedPremiumBoard]}`}
        style={{
          // Dynamic border for Spectrum board only
          ...(selectedPremiumBoard === 'spectrum' && {
            border: `2px solid ${borderColorRef.current}`,
            boxShadow: `0 0 10px ${borderColorRef.current}`,
          }),
        }}
      >
        <div
          className={`${styles["info-item"]} ${styles["info-item-board-" + selectedPremiumBoard]}`}
          style={infoItemDynamicStyle}
        >
          Timer: {player.time} s
        </div>

        {/* Pattern display for single player challenges */}
        {game.currentGameMode === "singlePlayerMode" &&
          game.singlePlayerMode.patternToMatch?.length > 0 && (
            <div
              className={`${styles["info-item"]} ${styles["info-item-board-" + selectedPremiumBoard]}`}
              style={infoItemDynamicStyle}
            >
              <div>pattern:</div>
              {game.singlePlayerMode.patternToMatch.map((element, index) => (
                <div
                  key={index}
                  style={{
                    border:
                      index === player.patternMatching.currentIndex
                        ? "2px solid black"
                        : "none",
                    display: "inline-block",
                    marginLeft: "2px",
                  }}
                >
                  {element}
                </div>
              ))}
            </div>
          )}

        <div
          className={`${styles["info-item"]} ${styles["info-item-board-" + selectedPremiumBoard]}`}
          style={infoItemDynamicStyle}
        >
          <div>
            Score: {player.stats.score}
          </div>
        </div>

        <div
          className={`${styles["info-item"]} ${styles["info-item-board-" + selectedPremiumBoard]}`}
          style={infoItemDynamicStyle}
        >
          life saver: {player.lifeSaverCount}
        </div>

        <div
          className={`${styles["info-item"]} ${styles["info-item-board-" + selectedPremiumBoard]}`}
          style={infoItemDynamicStyle}
        >
          1X blast: {player.stats.singleShots}
        </div>
        <div
          className={`${styles["info-item"]} ${styles["info-item-board-" + selectedPremiumBoard]}`}
          style={infoItemDynamicStyle}
        >
          2X blast: {player.stats.doubleShots}
        </div>
        <div
          className={`${styles["info-item"]} ${styles["info-item-board-" + selectedPremiumBoard]}`}
          style={infoItemDynamicStyle}
        >
          3X blast: {player.stats.tripleShots}
        </div>
        <div
          className={`${styles["info-item"]} ${styles["info-item-board-" + selectedPremiumBoard]}`}
          style={infoItemDynamicStyle}
        >
          4X blast: {player.stats.quadraShots}
        </div>
        <div
          className={`${styles["info-item"]} ${styles["info-item-board-" + selectedPremiumBoard]}`}
          style={infoItemDynamicStyle}
        >
          total blast:{" "}
          {player.stats.singleShots +
            player.stats.doubleShots * 2 +
            player.stats.tripleShots * 3 +
            player.stats.quadraShots * 4}
        </div>
      </div>

      {/* 3D Board Container */}
      <div
        className={`${styles["container"]} ${styles["container-board-" + selectedPremiumBoard]}`}
        style={{
          display: "flex",
          alignItems: "stretch",
          position: "relative",
          // Dynamic border for Spectrum board only
          ...(selectedPremiumBoard === 'spectrum' && {
            border: `2px solid ${borderColorRef.current}`,
            boxShadow: `0 0 10px ${borderColorRef.current}`,
          }),
        }}
      >
        {/* 3D Board - 200x400 matches 2D dimensions */}
        <div
          style={{
            width: "200px",
            height: "400px",
            position: "relative",
          }}
        >
          <TetrisBoard3D
            boardMatrix={player.boardMatrix}
            boardVersion={player.boardVersion || 0}
            currentPiece={player.currentTetromino}
            hardDropPreview={player.hardDropCoordinates}
            isLocalPlayer={true}
            onlineGame={player} // KEY: Pass player as onlineGame - same interface!
            skinName={skinName}
          />

          {player.isGameOver && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0,0,0,0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
                backdropFilter: "blur(3px)",
              }}
            >
              <span
                style={{
                  color: "#fff",
                  fontSize: "24px",
                  fontWeight: "bold",
                  textShadow: "0 0 10px rgba(255,0,0,0.8)",
                }}
              >
                GAME OVER
              </span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Player3DView;
